import _ from "underscore";

import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject } from "../../types/FabricCustomType";
import { IObjectChannel } from "../../types/HandlerObjectType";
import service from "../../service";
import { TooltipSeatChannel } from "../../components/tooltip/TooltipSeatChannel";
import store from "../../redux/store";
import { setSpinning } from "../../redux/designerPropertiesSlice"
import { isTouchDevice, isTouchInteraction } from "../../utils/InteractionUtil";
import { getSvgObject,findObjectInCanvas } from "../../utils/ObjectUtil";
import { Icons } from "../../enums/SeatIcon";
import { fabric } from "fabric";
interface SeatChannelPayloadType {
  eventId: string;
  seatChannels: SeatChannelType[]
}
interface SeatChannelType {
  channel: ChannelType;
  seats: SeatType[]
}

interface SeatType {
  parentKey: string;
  objectValue: string;
  objectKey: string;
}

interface ChannelType {
  name: string;
  color: string;
  key: string;
}

class Seat implements IObjectChannel {
  private canvasHandler: CanvasHandler;
  private targetSeat!: FabricObject<Object> | null;
  private selectedChannels: SeatChannelPayloadType = { seatChannels: [], eventId: '' };

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
  }

  public activateObjectHover(event: any, seat: FabricObject<Object>): any {
    if (this.targetSeat != seat) {
      this.deactivateObjectHover();//Elimina el hover del objeto anterior

      const seatCircle = seat.item(0);
      this.setTooltip(event, seat);
      seatCircle.set({
        radius: seatCircle.radius + 0.5,
      });
      this.targetSeat = seat;
      this.canvasHandler.canvas.requestRenderAll();
    }

    if (this.targetSeat)
      this.canvasHandler.canvas.setCursor("pointer");
    else
      this.canvasHandler.canvas.setCursor("default");
  }

  private async changeMultipleStatus(event: any): Promise<any> {
    this.canvasHandler.eventHandler.panning = false;

    let targetSeats: any[] = [];
    const selectionRect = event.selectionRect;

    if (selectionRect) {
      const { minX, maxX, minY, maxY } = selectionRect;
      const rows = this.canvasHandler.canvas.getObjects("group");
      rows.forEach((row: any) => {
        if (row.superType === "row") {
          const rowSeats = row.getObjects("group") || [];
          rowSeats.forEach((seat: any) => {
            if (seat.superType !== "seat") return;
            const matrix = seat.calcTransformMatrix();
            const absLeft = matrix[4];
            const absTop = matrix[5];

            // Verificamos si el centro absoluto del asiento entra en el recuadro que dibujó el usuario
            if (absLeft >= minX && absLeft <= maxX && absTop >= minY && absTop <= maxY) {
              targetSeats.push({ seat, rowKey: row.id });
            }
          });
        }
      });
    }

    if (targetSeats.length === 0) return;

    targetSeats.forEach((item: any) => {
      const {
        properties: { selectedChannel },
      } = store.getState();
      if (selectedChannel.key === '') {
        return;
      }
      this.temporalChangeChannelInCanvas(item.rowKey, item.seat, selectedChannel)
    });
    this.canvasHandler.canvas.discardActiveObject();
  }

  public async handleSelectObjects(event: any) {
    await this.changeMultipleStatus(event);
  }

  public async handleSelectObject(event: FabricEvent<MouseEvent | TouchEvent>, parentKey: string, seat: FabricObject<Object>) {
    const { properties: { selectedChannel }, } = store.getState();
    if (selectedChannel.key === '') return;

    this.temporalChangeChannelInCanvas(parentKey, seat, selectedChannel);

    if (isTouchDevice()) {
      this.deactivateObjectHover();
    } else {
      this.setTooltip(event, seat);
    }
  }

  public async handleObjectLockUpdate(object: any, hubItem: any, selectorKey: any): Promise<any> {
    if (object) {
      this.setChannelInCanvas(object, hubItem.channel);
      if (window.redseats.responseCallback)
        window.redseats.responseCallback();
    }
  }

  public deactivateObjectHover = () => {
    if (this.targetSeat) {
      this.canvasHandler.tooltipHandler.hide();
      this.canvasHandler.canvas.setCursor("default");
      const seatCircle = this.targetSeat.item(0);
      seatCircle.set({
        radius: seatCircle.radius - 0.5,
      });

      this.canvasHandler.canvas.requestRenderAll();
      this.targetSeat = null;
    } else {
      this.canvasHandler.tooltipHandler.hide();
      this.canvasHandler.canvas.setCursor("default");
    }
  };

  private async setChannelInCanvas(seat: any, channel: any) {
    if (!channel) return;
    const seatCircle = seat.item(0);
    const seatText = seat.item(1);
    const hasSpecialIcon = (seat.accesible || seat.companion || seat.restrictedView);

    channel.key = channel.key ?? "sn";
    const baseColor = seat.channel.key !== "sn" ? seat.channel.color : seat.statusColor.Free;
    const fill = new fabric.Color(baseColor).setAlpha(0.25).toRgba();
    const stroke = baseColor;

    seatCircle.set({
      fill: fill,
      stroke: stroke,
    });

    seat.set({
      channel: channel,
      isSelected: false
    });

    seatText.set({
      visible: !hasSpecialIcon && this.canvasHandler.isInteractionEnabled,
    });

    const seatOldIcon = seat.item(2);
    if (seatOldIcon)
      seat.remove(seatOldIcon);

    if (hasSpecialIcon && this.canvasHandler.isInteractionEnabled) {
      if (seat.restrictedView)
        seat.insertAt(getSvgObject(Icons.restricted, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.accesible)
        seat.insertAt(getSvgObject(Icons.accesible, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.companion)
        seat.insertAt(getSvgObject(Icons.companion, (seatCircle.radius * 1.7), '#000000'), 2, true);
    }

    this.canvasHandler.canvas.requestRenderAll();
  }

  private async updateSeatChannelInDb(channels: any) {
    channels.eventId = window.redseats.eventKey;
    await service.channel.changeSeatChannels(channels);
  }

  private setTooltip(event: any, seat: any) {
    let handleSelectObject;
    if (isTouchInteraction(event)) {
      handleSelectObject = () => this.handleSelectObject(event, event.target.id, seat);
    }
    const handleClose = () => {
      this.deactivateObjectHover();
    }
    const htmlElement = TooltipSeatChannel(seat, handleSelectObject, handleClose);
    this.canvasHandler.tooltipHandler.show(event.e, htmlElement);
  }

  public async handleSaveChanges() {
    store.dispatch(setSpinning(true));
    if (this.selectedChannels.seatChannels.length > 0) {
      let channelIndex = this.selectedChannels.seatChannels.findIndex(channel => channel.channel.key === 'sn');
      if (channelIndex >= 0) {
        this.selectedChannels.seatChannels[channelIndex].channel.key = null;
      }
      await this.updateSeatChannelInDb(this.selectedChannels);
    }
    this.selectedChannels.seatChannels = [];
    store.dispatch(setSpinning(false));
  }

  private addSeatChannels(parentKey: string, seat: any) {
    let channelIndex = this.selectedChannels.seatChannels.findIndex(channel => channel.channel.key === seat.channel.key);
    if (channelIndex >= 0) {
      this.selectedChannels.seatChannels[channelIndex].seats.push({
        objectValue: seat.value,
        objectKey: seat.id,
        parentKey: parentKey
      });
    }
    else {
      this.selectedChannels.seatChannels.push({
        channel: {
          color: seat.channel.color,
          key: seat.channel.key,
          name: seat.channel.name
        },
        seats: [{
          objectValue: seat.value,
          objectKey: seat.id,
          parentKey: parentKey
        }]
      })
    }
  }

  private removeSeatChannels(seat: any) {
    if (seat.channel) {
      let channelIndex = this.selectedChannels.seatChannels.findIndex(channel => channel.channel.key === seat.channel.key);
      if (channelIndex >= 0) {
        let seatIndex = this.selectedChannels.seatChannels[channelIndex].seats.findIndex(seatC => seatC.objectKey === seat.id);
        if (seatIndex >= 0) {
          this.selectedChannels.seatChannels[channelIndex].seats.splice(seatIndex, 1);
        }
      }
    }
  }

  private temporalChangeChannelInCanvas(parentKey: string, seat: any, selectedChannel: ChannelType) {
    this.removeSeatChannels(seat);
    const seatCircle = seat.item(0);
    const seatText = seat.item(1);
    const hasSpecialIcon = (seat.accesible || seat.companion || seat.restrictedView);

    if (seat.isSelected) {
      seat.set({
        channel: {
          color: seat.previousChannel.color,
          name: seat.previousChannel.name,
          key: seat.previousChannel.key
        },
        isSelected: false
      });
      const baseColor = seat.channel.key !== "sn" ? seat.channel.color : seat.statusColor.Free;
      const fill = new fabric.Color(baseColor).setAlpha(0.25).toRgba();
      const stroke = baseColor;

      seatCircle.set({ fill: fill, stroke: stroke });

      seatText.set({
        visible: !hasSpecialIcon && this.canvasHandler.isInteractionEnabled,
      });

      const seatOldIcon = seat.item(2);
      if (seatOldIcon)
        seat.remove(seatOldIcon);

      if (hasSpecialIcon && this.canvasHandler.isInteractionEnabled) {
        if (seat.restrictedView)
          seat.insertAt(getSvgObject(Icons.restricted, (seatCircle.radius * 1.7), '#000000'), 2, true);
        else if (seat.accesible)
          seat.insertAt(getSvgObject(Icons.accesible, (seatCircle.radius * 1.7), '#000000'), 2, true);
        else if (seat.companion)
          seat.insertAt(getSvgObject(Icons.companion, (seatCircle.radius * 1.7), '#000000'), 2, true);
      }
    }
    else {
      if (seat.channel.key === selectedChannel.key) return;

      seat.set({
        channel: {
          color: selectedChannel.color,
          name: selectedChannel.name,
          key: selectedChannel.key
        },
        previousChannel: {
          color: seat.channel.color,
          name: seat.channel.name,
          key: seat.channel.key
        },
        isSelected: true
      });

      seatCircle.set({
        fill: selectedChannel.color,
        stroke: selectedChannel.color,
      });

      seatText.set({
        visible: false,
      });

      const seatOldIcon = seat.item(2);
      if (seatOldIcon)
        seat.remove(seatOldIcon);

      const seatCheckIcon = getSvgObject(Icons.check, (seatCircle.radius * 1.7), '#ffffff');
      seatCheckIcon.set({
        angle: seatText.angle,
      });
      seat.insertAt(seatCheckIcon, 2, true);

      this.addSeatChannels(parentKey, seat);
    }
    this.canvasHandler.canvas.requestRenderAll();
  }
}
export default Seat;
